import { Component, inject, OnInit } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { PARENT_FORM_ROW_GAP } from '@app/finxone-web-frontend/app/lib/utils/di-tokens';
import type { BeneficiaryFormType } from '@app/finxone-web-frontend/app/Modules/shared/widgets/add-beneficiary-v2/beneficiary-form.service';
import {
  BeneficiaryFormService,
  FormGroupControls,
} from '@app/finxone-web-frontend/app/Modules/shared/widgets/add-beneficiary-v2/beneficiary-form.service';
import { setFieldValidation } from '@app/finxone-web-frontend/app/Modules/shared/widgets/add-beneficiary-v2/beneficiary-v2.utils';
import {
  BeneficiaryRequirementsResponse,
  CommonUiZoneWidgetConfig,
  CustomValidators,
  FormFieldConfig,
  ProviderClass,
  UiZoneWidgetAttributeConfig,
  WidgetLayoutAttributes,
} from '@finxone-platform/shared/sys-config-types';

@Component({
  standalone: false,
  selector: 'finxone-web-frontend-beneficiary-credentials-widget',
  templateUrl: './beneficiary-credentials-widget.component.html',
  styleUrl: './beneficiary-credentials-widget.component.css',
})
export class BeneficiaryCredentialsWidgetComponent implements OnInit {
  public beneficiaryForm: FormGroup<FormGroupControls<BeneficiaryFormType>>;
  public inputFieldConfigs: FormFieldConfig[];
  public loading = true;
  public error = false;

  public parentFormWidgetRowGap: string | undefined | null = inject(PARENT_FORM_ROW_GAP, { optional: true });

  constructor(
    public dataIn: ProviderClass<WidgetLayoutAttributes>,
    public beneficiaryFormService: BeneficiaryFormService,
  ) {}

  public ngOnInit(): void {
    this.beneficiaryForm = this.beneficiaryFormService.beneficiaryForm;

    const beneficiaryType = this.beneficiaryForm.get('type')?.value;

    if (this.dataIn?.attri['childComponents']) {
      this.inputFieldConfigs = this.dataIn.attri['childComponents'].map(
        (widget: CommonUiZoneWidgetConfig) => {
          const widgetAttri = widget.attributes as UiZoneWidgetAttributeConfig;
          return widgetAttri?.widgetProperties?.inputFieldConfig as FormFieldConfig;
        },
      );

      this.beneficiaryFormService.getAndSetBeneficiaryRequirements().subscribe({
        next: (beneficiaryRequirements: BeneficiaryRequirementsResponse) => {
          const bankAccount = beneficiaryRequirements.beneficiaryBankAccount;

          if (bankAccount) {
            // Full Name is always required for bank accounts
            setFieldValidation('fullName', this.inputFieldConfigs, 'required', true);

            if (beneficiaryType === 'personal') {
              // // add validation for a first and last name if personal beneficiary
              const fieldIndex = this.inputFieldConfigs.findIndex((f) => f.name === 'fullName');
              if (fieldIndex !== -1) {
                this.inputFieldConfigs[fieldIndex] = {
                  ...this.inputFieldConfigs[fieldIndex],
                  validation: {
                    ...(this.inputFieldConfigs[fieldIndex].validation ?? {}),
                    validators: [
                      {
                        type: CustomValidators.NAME_IS_FIRST_AND_LAST_VALIDATOR,
                      },
                    ],
                  },
                };
              }
            }

            // IBAN
            if (bankAccount.iban?.requirement === 'required') {
              setFieldValidation('iban', this.inputFieldConfigs, 'required', true);
            }
            if (bankAccount.iban?.regex) {
              setFieldValidation('iban', this.inputFieldConfigs, 'pattern', bankAccount.iban?.regex);
            }
            // Account Number
            if (bankAccount.accountNumber?.requirement === 'required') {
              setFieldValidation('accountNumber', this.inputFieldConfigs, 'required', true);
            }
            if (bankAccount.accountNumber?.regex) {
              setFieldValidation(
                'accountNumber',
                this.inputFieldConfigs,
                'pattern',
                bankAccount.accountNumber?.regex,
              );
            }
            // Bank Name
            if (bankAccount.bankName?.requirement === 'required') {
              setFieldValidation('bankName', this.inputFieldConfigs, 'required', true);
            }
            if (bankAccount.bankName.regex) {
              setFieldValidation('bankName', this.inputFieldConfigs, 'pattern', bankAccount.bankName.regex);
            }
            // Routing Codes
            if (bankAccount.routingCodes) {
              Object.entries(bankAccount.routingCodes).forEach(([key, value]) => {
                if (value?.requirement === 'required') {
                  let controlName = '';
                  switch (key) {
                    case 'bic':
                      controlName = 'BIC';
                      break;
                    case 'aba':
                      controlName = 'aba';
                      break;
                    case 'bank-code':
                      controlName = 'sortCode';
                      break;
                    case 'branch-code':
                      controlName = 'branchCode';
                      break;
                    case 'rtn-canada':
                      controlName = 'rtnCanada';
                      break;
                    case 'sort-code':
                      controlName = 'sortCode';
                      break;
                    case 'bsb-code':
                      controlName = 'bsbCode';
                      break;
                    case 'cnaps':
                      controlName = 'cnaps';
                      break;
                    case 'ifsc':
                      controlName = 'ifsc';
                      break;
                    case 'clabe':
                      controlName = 'clabe';
                      break;
                    default:
                      controlName = key;
                  }
                  setFieldValidation(controlName, this.inputFieldConfigs, 'required', true);
                  if (value.regex) {
                    setFieldValidation(controlName, this.inputFieldConfigs, 'pattern', value.regex);
                  }
                }
              });
            }
          }
          this.loading = false; // Set loading to false after configs are ready
        },
        error: (err) => {
          console.error('Error loading beneficiary requirements', err);
          this.loading = false;
          this.error = true; // Set error to true if there is an error loading requirements
        },
      });
    } else {
      this.loading = false; // Set loading to false if no configs to load
    }
  }
}
